import { useEffect } from 'react'
import { Col, Container, Form, Row } from 'react-bootstrap'
import { useSelector, useDispatch } from 'react-redux'
import type { ThunkDispatch, UnknownAction } from '@reduxjs/toolkit'
import moment from 'moment'
import { camelCase } from 'lodash'
import { ButtonModals, Checkbox, DataList, ErrorModal, selectCommon } from '../common'
import type { DataListColumn } from '../common'
import { useAuth } from '../auth'
import {
  fetchUsers,
  modifyUsers,
  selectUser,
  selectColumn,
  selectPage,
  selectId,
  unselectId,
  openModal,
  closeModal,
  setSearchCondition,
  dismissError,
  UserEnableDisableForm,
  UserUpdateForm
} from '../user'
import { modifyUser } from './userAPI'
import { User } from './types'
moment.locale('ja')

type UserUpdateFormValues = Pick<User, 'id' | 'name' | 'email' | 'role'>

interface EnumValues {
  userRoles: Record<string, string>
}

export const UserList = () => {
  const {
    data: rawData,
    currentPage,
    totalPages,
    sort,
    selectedIds,
    searchCondition,
    openedModal,
    fetching,
    errors
  } = useSelector(selectUser)
  const data = rawData as User[]
  const { currentUser } = useAuth()
  const { enumValues } = useSelector(selectCommon) as unknown as { enumValues: EnumValues }
  const dispatch = useDispatch<ThunkDispatch<unknown, unknown, UnknownAction>>()
  useEffect(() => {
    dispatch(fetchUsers({ page: currentPage, sort, search: searchCondition }))
  }, [dispatch, sort, currentPage, searchCondition])

  const handleColumnClick = (columnName: string) => () => dispatch(selectColumn(columnName))

  const handlePaginationClick = (page: number) => dispatch(selectPage(page))

  const columns: DataListColumn[] = [
    {
      id: 'checkboxColumn',
      meta: { cellProps: { style: { width: '40px', textAlign: 'center' } } },
      cell: (props: { row: { original: User } }) => {
        const user = props.row.original
        const userId = parseInt(String(user.id))
        return (
          <Checkbox
            value={user.id}
            checked={selectedIds.includes(userId)}
            disabled={fetching || currentUser?.id === userId}
            onChange={(value: string, checked: boolean) => {
              dispatch((checked ? selectId : unselectId)(parseInt(value)))
            }}
          />
        )
      }
    },
    { name: 'ID', accessorKey: 'id', sortKey: 'id' },
    { name: '名称', accessorKey: 'name', sortKey: 'name' },
    { name: 'メールアドレス', accessorKey: 'email', sortKey: 'email' },
    {
      name: 'ロール',
      accessorKey: 'role',
      sortKey: 'role',
      cell: (info) => enumValues.userRoles[camelCase(info.getValue() as string)]
    },
    {
      name: 'メールアドレスの確認',
      accessorKey: 'confirmedAt',
      sortKey: 'confirmedAt',
      cell: (info) => {
        const val = info.getValue() as string | null
        return val ? moment(val).format('LLL') + ' 確認済み' : '未確認'
      }
    },
    { id: 'group', name: 'グループ', accessorFn: ({ group }: User) => (group ? group.name : '-') },
    {
      name: '有効 / 無効',
      accessorKey: 'enable',
      sortKey: 'enable',
      cell: (info) => ((info.getValue() as boolean) ? '有効' : '無効')
    }
  ]
  const handleDisableSubmit = () => {
    if (selectedIds.length < 1) {
      return
    }

    dispatch(
      modifyUsers({
        disablingUsers: { ids: selectedIds }
      })
    )
  }
  const handleEnableSubmit = () => {
    if (selectedIds.length < 1) {
      return
    }

    dispatch(
      modifyUsers({
        enablingUsers: { ids: selectedIds }
      })
    )
  }
  const singleSelectedUser = selectedIds.length === 1 ? data.find((item) => item.id === selectedIds[0]) : null
  const isSelectedUserReadable = singleSelectedUser
  const buttonModalsEnabled = !fetching && currentUser?.role !== 'operator'
  const handleUpdateUserSubmit = ({ ...props }: UserUpdateFormValues) => {
    dispatch(modifyUser({ user: props }))
  }
  const toolSettings = [
    {
      name: 'disable',
      button: {
        handleClick: () => {
          dispatch(openModal('disable'))
        },
        disabled: !buttonModalsEnabled || selectedIds.length === 0,
        icon: 'times',
        variant: 'outline-danger',
        text: '無効化'
      },
      form: {
        initialValues: {
          selectedIds
        },
        handleSubmit: handleDisableSubmit,
        Component: UserEnableDisableForm,
        props: {
          title: 'ユーザ無効化',
          fetching,
          selectedIds,
          users: data,
          onClickCancel: () => dispatch(closeModal()),
          message: '以下のユーザを無効化します。よろしいですか？',
          submitButtonText: '無効化する'
        }
      }
    },
    {
      name: 'enable',
      button: {
        handleClick: () => {
          dispatch(openModal('enable'))
        },
        disabled: !buttonModalsEnabled || selectedIds.length === 0,
        icon: 'user',
        text: '有効化'
      },
      form: {
        initialValues: {
          selectedIds
        },
        handleSubmit: handleEnableSubmit,
        Component: UserEnableDisableForm,
        props: {
          title: 'ユーザ有効化',
          fetching,
          selectedIds,
          users: data,
          onClickCancel: () => dispatch(closeModal()),
          message: '以下のユーザを有効化します。よろしいですか？',
          submitButtonText: '有効化する'
        }
      }
    },
    {
      name: 'edit',
      button: {
        handleClick: () => {
          dispatch(openModal('edit'))
        },
        disabled: !buttonModalsEnabled || !isSelectedUserReadable,
        icon: 'edit',
        text: 'ユーザー情報編集'
      },
      form: {
        initialValues: () => {
          if (!singleSelectedUser) {
            return
          }
          return {
            id: singleSelectedUser.id,
            name: singleSelectedUser.name,
            email: singleSelectedUser.email,
            role: singleSelectedUser.role
          }
        },
        handleSubmit: handleUpdateUserSubmit,
        Component: UserUpdateForm,
        validationSchema: UserUpdateForm.schema,
        props: {
          title: 'ユーザー情報編集',
          fetching,
          selectedIds,
          onClickCancel: () => dispatch(closeModal()),
          submitButtonText: '変更する',
          enumValues
        }
      }
    }
  ]

  return (
    <Container fluid>
      <ErrorModal errors={errors} onClose={(errorKey) => dispatch(dismissError(errorKey))} />
      {buttonModalsEnabled && (
        <Row className='mb-3 align-items-center'>
          <Col sm='auto'>
            <ButtonModals settings={toolSettings} openedModal={openedModal} />
          </Col>
          <Col sm className='d-flex justify-content-end'>
            <Form>
              <Form.Check
                type='switch'
                id='includeDisabledSwitch'
                name='includeDisabled'
                label='無効なユーザを含めて表示'
                checked={searchCondition.includeDisabled}
                onChange={(e) => {
                  const newValue = e.target.checked
                  const name = 'includeDisabled'
                  dispatch(setSearchCondition({ name, value: newValue }))
                }}
              />
            </Form>
          </Col>
        </Row>
      )}
      <DataList
        columns={columns}
        data={data}
        currentPage={currentPage}
        totalPages={totalPages}
        onColumnClick={handleColumnClick}
        sort={sort}
        onPaginationClick={handlePaginationClick}
      />
    </Container>
  )
}
